/* eslint-disable @typescript-eslint/no-explicit-any */
import { CustomColumnTypes } from "@/enum/list.enum";

import { ListPerson } from "../../../../List/schema/list";
import { ColumnValueInfo } from "../../../../List/schema/columnValue";

const escapeHtml = (s: string): string =>
	s
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");

const SNIPPET_CLASS = "bg-yellow rounded-sm px-0.5 border-b-2 border-status-warning";
const VARIABLE_CLASS = "bg-blue-action-200 text-blue-action rounded px-1 font-medium";
const SPINTAX_CLASS = "bg-spintax-light text-spintax rounded px-1 font-medium";

const lookupVariable = (lead: ListPerson | undefined, key: string): string | undefined => {
	if (!lead) {
		return undefined;
	}

	const ctx: any = {
		prospect: lead.prospect,
		list: lead.list,
		person: lead.prospect,
	};

	const parts = key.split("_");
	let cursor: any = ctx;

	for (const p of parts) {
		if (cursor == null) {
			return undefined;
		}

		cursor = Array.isArray(cursor[p]) ? cursor[p][0] : cursor[p];
	}

	if (cursor == null || typeof cursor === "object") {
		return undefined;
	}

	return String(cursor);
};

/**
 * Resolves a spintax (RANDOM_VALUE) column for the lead: the per-person
 * stored value when one exists, otherwise the column's first configured
 * option — mirroring the backend's run-prompt fallback. Returns undefined
 * for non-spintax columns so the caller can leave the token untouched.
 */
const lookupSpintaxValue = (lead: ListPerson | undefined, columnId: number): string | undefined => {
	const columnValue = lead?.columns?.find((cv) => cv.columnConfigId === columnId);
	const columnConfig =
		columnValue?.columnConfig ?? lead?.list?.columns?.find((c) => c.id === columnId);

	if (columnConfig?.type !== CustomColumnTypes.RANDOM_VALUE) {
		return undefined;
	}

	const stored = columnValue?.value;

	if (stored != null && String(stored) !== "") {
		return String(stored);
	}

	const options = Array.isArray(columnConfig.config) ? columnConfig.config : [];

	return options.length > 0 ? String(options[0]) : undefined;
};

/**
 * Wraps every `{column_<id>_*}` token in a yellow highlight (using the AI
 * output for that column from `columnValues`), spintax (RANDOM_VALUE)
 * columns in a purple highlight (using the lead's stored value or one of
 * the configured options) and every `{prospect_*}` / `{key|label}` variable
 * in a blue highlight (using the lead's data).
 *
 * Unknown tokens are left untouched. Unlike using the backend's already-
 * substituted `result.message`, this preserves the exact token positions so
 * we can colorize each substitution.
 */
export const colorizeMessage = (
	originalHtml: string,
	lead: ListPerson | undefined,
	columnValues: ColumnValueInfo[]
): string => {
	if (!originalHtml) {
		return "";
	}

	const snippetById = new Map<number, string>();

	for (const cv of columnValues) {
		const id = cv.columnConfig?.id ?? cv.columnConfigId;

		if (id == null) {
			continue;
		}

		snippetById.set(id, String(cv.value ?? ""));
	}

	return originalHtml.replace(/\{([^{}|]+)(?:\|[^{}]*)?\}/g, (match, rawKey) => {
		const key = String(rawKey).trim();

		if (key.startsWith("column_")) {
			const idMatch = key.match(/^column_(\d+)_/);

			if (idMatch) {
				const id = Number(idMatch[1]);
				const text = snippetById.get(id);

				if (text != null && text !== "") {
					// AI output may contain its own formatting — pass through.
					return `<span class="${SNIPPET_CLASS}">${text}</span>`;
				}

				const spintaxValue = lookupSpintaxValue(lead, id);

				if (spintaxValue !== undefined) {
					return `<span class="${SPINTAX_CLASS}">${escapeHtml(spintaxValue)}</span>`;
				}
			}

			return match;
		}

		const resolved = lookupVariable(lead, key);

		if (resolved !== undefined) {
			return `<span class="${VARIABLE_CLASS}">${escapeHtml(resolved)}</span>`;
		}

		return match;
	});
};
